// components/RecordingStudio/ScriptGeneratorModal.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Sparkles,
  Loader,
  ChevronDown,
  RotateCcw,
  Check,
  Copy,
  Wand2,
  Mic,
  Clock,
  Target,
  Lightbulb,
} from "lucide-react";
import api from "../../services/api";

// ── Tone options ─────────────────────────────────────────────────────────────
const TONES = [
  { value: "professional", label: "Professional", emoji: "💼" },
  { value: "friendly", label: "Friendly", emoji: "😊" },
  { value: "energetic", label: "Energetic", emoji: "⚡" },
  { value: "calm", label: "Calm & Soothing", emoji: "🌊" },
  { value: "authoritative", label: "Authoritative", emoji: "🎯" },
  { value: "conversational", label: "Conversational", emoji: "💬" },
  { value: "dramatic", label: "Dramatic", emoji: "🎭" },
  { value: "humorous", label: "Humorous", emoji: "😄" },
];

const DURATIONS = [
  { value: 15, label: "15 sec", words: "~40 words" },
  { value: 30, label: "30 sec", words: "~75 words" },
  { value: 60, label: "1 min", words: "~150 words" },
  { value: 90, label: "90 sec", words: "~225 words" },
  { value: 120, label: "2 min", words: "~300 words" },
];

const PURPOSES = [
  "Podcast intro / outro",
  "Advertisement / promo",
  "Explainer / how-to",
  "Product demo narration",
  "Corporate announcement",
  "Audio book narration",
  "IVR / phone system",
  "YouTube video narration",
  "Training / e-learning",
  "Social media reel",
];

// ── Pill select ───────────────────────────────────────────────────────────────
const PillSelect = ({
  options,
  value,
  onChange,
  keyField = "value",
  labelField = "label",
}) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((opt) => {
      const key = typeof opt === "string" ? opt : opt[keyField];
      const label = typeof opt === "string" ? opt : opt[labelField];
      const active = value === key;
      return (
        <button
          key={key}
          onClick={() => onChange(key)}
          className="px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all"
          style={{
            background: active
              ? "rgba(139,92,246,0.2)"
              : "rgba(255,255,255,0.04)",
            border: active
              ? "1px solid rgba(139,92,246,0.5)"
              : "1px solid rgba(255,255,255,0.07)",
            color: active ? "#c4b5fd" : "#6b7280",
          }}
        >
          {typeof opt !== "string" && opt.emoji ? `${opt.emoji} ` : ""}
          {label}
        </button>
      );
    })}
  </div>
);

// ── Typing animation for streaming text ──────────────────────────────────────
const StreamingText = ({ text, done }) => {
  return (
    <span>
      {text}
      {!done && (
        <span
          className="inline-block w-0.5 h-3.5 ml-0.5 rounded-full align-middle bg-violet-400"
          style={{ animation: "blink 0.8s step-end infinite" }}
        />
      )}
    </span>
  );
};

// ── Main modal ────────────────────────────────────────────────────────────────
const ScriptGeneratorModal = ({ onClose, onUseScript, voiceDisplayName }) => {
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("professional");
  const [duration, setDuration] = useState(30);
  const [purpose, setPurpose] = useState("");
  const [generatedScript, setGeneratedScript] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const textareaRef = useRef(null);
  const outputRef = useRef(null);

  useEffect(() => {
    if (generatedScript && outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [generatedScript]);

  const buildPrompt = () => {
    const selectedDur = DURATIONS.find((d) => d.value === duration);
    const selectedTone = TONES.find((t) => t.value === tone);
    return [
      `Write a voice-over script with the following requirements:`,
      `Topic / Brief: ${prompt}`,
      `Tone: ${selectedTone?.label || tone}`,
      `Target duration: ${selectedDur?.label} (approximately ${selectedDur?.words})`,
      purpose ? `Purpose: ${purpose}` : null,
      voiceDisplayName
        ? `This will be read by a voice called "${voiceDisplayName}" — write accordingly.`
        : null,
      additionalNotes ? `Additional instructions: ${additionalNotes}` : null,
      ``,
      `Guidelines:`,
      `- Write ONLY the script text, no stage directions, no headers, no labels`,
      `- Use natural spoken language — short sentences, easy to read aloud`,
      `- No markdown formatting, no asterisks, no bullet points`,
      `- Aim for exactly the target word count to fit the duration`,
      `- Make every word count — no filler phrases`,
    ]
      .filter(Boolean)
      .join("\n");
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setIsGenerating(true);
    setIsDone(false);
    setGeneratedScript("");
    setError(null);

    try {
      await api.generateScript(
        {
          prompt,
          tone,
          duration,
          purpose,
          voiceDisplayName: voiceDisplayName || "",
          additionalNotes,
        },
        {
          onDelta: (text) => {
            setGeneratedScript((prev) => prev + text);
          },
          onDone: () => {
            setIsDone(true);
          },
          onError: (msg) => {
            setError(msg);
          },
        },
      );
    } catch (err) {
      setError(err.message || "Generation failed. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedScript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUse = () => {
    if (generatedScript.trim()) {
      onUseScript(generatedScript.trim());
      onClose();
    }
  };

  const wordCount = generatedScript.trim().split(/\s+/).filter(Boolean).length;
  const estimatedSecs = Math.round((wordCount / 150) * 60);

  return (
    <>
      <style>{`
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(16px)" }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {/* Modal */}
        <div
          className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-3xl overflow-hidden shadow-2xl"
          style={{
            background: "linear-gradient(180deg, #0f1117 0%, #0a0c12 100%)",
            border: "1px solid rgba(255,255,255,0.07)",
            boxShadow:
              "0 40px 100px rgba(0,0,0,0.8), 0 0 0 1px rgba(139,92,246,0.1)",
            animation: "fadeSlideIn 0.2s ease-out",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-6 py-5 flex-shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                }}
              >
                <Sparkles size={16} className="text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white tracking-tight leading-none">
                  AI Script Generator
                </h2>
                <p className="text-[10px] text-gray-600 mt-0.5">
                  Describe what you need — AI writes the perfect script
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-600 hover:text-white transition-colors"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
              }}
            >
              <X size={15} />
            </button>
          </div>

          {/* Body — scrollable */}
          <div
            className="flex-1 overflow-y-auto"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(139,92,246,0.2) transparent",
            }}
          >
            <div className="px-6 py-5 space-y-5">
              {/* Main prompt */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                  What should the script be about?
                </label>
                <textarea
                  ref={textareaRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                      handleGenerate();
                  }}
                  rows={3}
                  placeholder="e.g. An engaging intro for a podcast about personal finance for millennials…"
                  className="w-full p-3.5 rounded-xl text-sm text-gray-200 placeholder-gray-700 resize-none focus:outline-none leading-relaxed transition-all"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "rgba(139,92,246,0.4)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(255,255,255,0.07)";
                  }}
                />
                <p className="text-[10px] text-gray-700">Tip: ⌘↵ to generate</p>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                  <Clock size={9} />
                  Target Duration
                </label>
                <div className="flex gap-2">
                  {DURATIONS.map((d) => (
                    <button
                      key={d.value}
                      onClick={() => setDuration(d.value)}
                      className="flex-1 py-2 rounded-xl text-center transition-all"
                      style={{
                        background:
                          duration === d.value
                            ? "rgba(139,92,246,0.15)"
                            : "rgba(255,255,255,0.03)",
                        border:
                          duration === d.value
                            ? "1px solid rgba(139,92,246,0.4)"
                            : "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      <div
                        className="text-[11px] font-bold"
                        style={{
                          color: duration === d.value ? "#c4b5fd" : "#6b7280",
                        }}
                      >
                        {d.label}
                      </div>
                      <div className="text-[9px] text-gray-700 mt-0.5">
                        {d.words}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tone */}
              <div className="space-y-2">
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                  <Target size={9} />
                  Tone
                </label>
                <PillSelect options={TONES} value={tone} onChange={setTone} />
              </div>

              {/* Purpose */}
              <div className="space-y-2">
                <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                  <Mic size={9} />
                  Purpose (optional)
                </label>
                <PillSelect
                  options={PURPOSES}
                  value={purpose}
                  onChange={(v) => setPurpose(v === purpose ? "" : v)}
                />
              </div>

              {/* Advanced */}
              <div>
                <button
                  onClick={() => setShowAdvanced((p) => !p)}
                  className="flex items-center gap-1.5 text-[10px] text-gray-600 hover:text-gray-400 transition-colors py-0.5"
                >
                  <ChevronDown
                    size={10}
                    style={{
                      transform: showAdvanced ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                  Additional instructions
                </button>
                {showAdvanced && (
                  <textarea
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                    rows={2}
                    placeholder="e.g. Include a strong call-to-action at the end. Mention our 30-day free trial."
                    className="mt-2 w-full p-3 rounded-xl text-xs text-gray-300 placeholder-gray-700 resize-none focus:outline-none leading-relaxed"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.06)",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "rgba(139,92,246,0.3)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "rgba(255,255,255,0.06)";
                    }}
                  />
                )}
              </div>

              {/* Output area */}
              {(generatedScript || isGenerating) && (
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{ border: "1px solid rgba(139,92,246,0.2)" }}
                >
                  {/* Output header */}
                  <div
                    className="flex items-center justify-between px-4 py-2.5"
                    style={{
                      background: "rgba(139,92,246,0.08)",
                      borderBottom: "1px solid rgba(139,92,246,0.12)",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Wand2 size={10} className="text-violet-400" />
                      <span className="text-[10px] font-bold text-violet-400 uppercase tracking-wider">
                        Generated Script
                      </span>
                      {generatedScript && (
                        <span className="text-[9px] text-gray-600 font-mono">
                          {wordCount}w · ~{estimatedSecs}s
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {generatedScript && (
                        <>
                          <button
                            onClick={() => {
                              setGeneratedScript("");
                              setIsDone(false);
                            }}
                            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-600 hover:text-gray-300 transition-colors"
                            title="Clear"
                          >
                            <RotateCcw size={10} />
                          </button>
                          <button
                            onClick={handleCopy}
                            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-600 hover:text-gray-300 transition-colors"
                            title="Copy"
                          >
                            {copied ? (
                              <Check size={10} className="text-emerald-400" />
                            ) : (
                              <Copy size={10} />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Script text */}
                  <div
                    ref={outputRef}
                    className="p-4 max-h-52 overflow-y-auto"
                    style={{
                      background: "rgba(0,0,0,0.2)",
                      scrollbarWidth: "thin",
                      scrollbarColor: "rgba(139,92,246,0.15) transparent",
                    }}
                  >
                    {isGenerating && !generatedScript ? (
                      <div className="flex items-center gap-2 py-2">
                        <div className="flex gap-1">
                          {[0, 1, 2].map((i) => (
                            <div
                              key={i}
                              className="w-1.5 h-1.5 rounded-full bg-violet-500"
                              style={{
                                animation: `blink 1.2s ease-in-out ${i * 0.2}s infinite`,
                              }}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-gray-600">
                          Writing your script…
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">
                        <StreamingText text={generatedScript} done={isDone} />
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div
                  className="flex items-start gap-2.5 px-4 py-3 rounded-xl"
                  style={{
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.2)",
                  }}
                >
                  <Lightbulb
                    size={12}
                    className="text-red-400 flex-shrink-0 mt-0.5"
                  />
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center gap-3 px-6 py-4 flex-shrink-0"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          >
            <button
              onClick={handleGenerate}
              disabled={!prompt.trim() || isGenerating}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: isGenerating
                  ? "rgba(124,58,237,0.4)"
                  : "linear-gradient(135deg, #7c3aed, #6d28d9)",
                boxShadow: isGenerating
                  ? "none"
                  : "0 4px 16px rgba(124,58,237,0.35)",
                minWidth: "140px",
              }}
            >
              {isGenerating ? (
                <>
                  <Loader size={13} className="animate-spin" />
                  Generating…
                </>
              ) : generatedScript ? (
                <>
                  <RotateCcw size={13} />
                  Regenerate
                </>
              ) : (
                <>
                  <Sparkles size={13} />
                  Generate Script
                </>
              )}
            </button>

            {generatedScript && isDone && (
              <button
                onClick={handleUse}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all"
                style={{
                  background: "rgba(16,185,129,0.12)",
                  border: "1px solid rgba(16,185,129,0.3)",
                  color: "#34d399",
                }}
              >
                <Check size={13} />
                Use This Script
              </button>
            )}

            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm text-gray-600 hover:text-gray-300 transition-colors"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScriptGeneratorModal;
